"use client";

import { createContext, type ReactNode, useContext, useMemo } from "react";
import { AI_CHAT_VISUALIZATION_STORAGE_KEY } from "@/lib/ai/constants";
import { StorageManager } from "@/lib/storage/local-storage";

const VISUALIZATION_STORAGE_ENABLED_VALUE = "enabled";
const VISUALIZATION_STORAGE_DISABLED_VALUE = "disabled";

export type VisualizationUiContextType = {
  isVisualizationEnabled: boolean;
  setVisualizationEnabled: (enabled: boolean) => void;
};

const VisualizationUiContext = createContext<VisualizationUiContextType | null>(
  null,
);

/**
 * 可視化トグルの保存値を boolean へ戻す。
 *
 * @remarks
 * page / side panel の両方で同じ preference を復元するため、storage の decode は
 * この helper に集約する。不正値は URL 正規化や初期 UI をぶらさないよう破棄する。
 */
export function readStoredVisualizationEnabled(): boolean | null {
  const savedValue = StorageManager.getItem(AI_CHAT_VISUALIZATION_STORAGE_KEY);
  if (savedValue === VISUALIZATION_STORAGE_ENABLED_VALUE) {
    return true;
  }
  if (savedValue === VISUALIZATION_STORAGE_DISABLED_VALUE) {
    return false;
  }
  if (savedValue) {
    StorageManager.removeItem(AI_CHAT_VISUALIZATION_STORAGE_KEY);
  }
  return null;
}

/**
 * 可視化トグルの現在値を、URL 未指定時の既定値として保存する。
 */
export function writeStoredVisualizationEnabled(enabled: boolean): void {
  StorageManager.setItem(
    AI_CHAT_VISUALIZATION_STORAGE_KEY,
    enabled
      ? VISUALIZATION_STORAGE_ENABLED_VALUE
      : VISUALIZATION_STORAGE_DISABLED_VALUE,
  );
}

/**
 * 可視化 plugin 用の UI state を共有する。
 *
 * @remarks
 * visualization の ON/OFF は chat 共通 context に混ぜず、この provider 配下だけで
 * 扱う。これにより shared chat 契約へ feature 固有 state を漏らさない。
 */
export function VisualizationUiProvider({
  children,
  isVisualizationEnabled,
  setVisualizationEnabled,
}: VisualizationUiContextType & {
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({
      isVisualizationEnabled,
      setVisualizationEnabled,
    }),
    [isVisualizationEnabled, setVisualizationEnabled],
  );

  return (
    <VisualizationUiContext.Provider value={value}>
      {children}
    </VisualizationUiContext.Provider>
  );
}

/**
 * visualization plugin が UI 設定へアクセスするための hook。
 *
 * provider 不在は composition の不整合なので fail-fast する。
 */
export function useVisualizationUiContext() {
  const context = useContext(VisualizationUiContext);
  if (!context) {
    throw new Error(
      "useVisualizationUiContext must be used within a VisualizationUiProvider",
    );
  }
  return context;
}
